import { Badge, Button, Card, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowResponse } from "@/api";
import { authorizeFlowMutation, errorMessage, getFlowAuthorizationOptions, getFlowAuthorizationQueryKey } from "@/api";
import { GoogleConsentButton } from "@/auth/google/GoogleConsentButton";
import { Text } from "@/components/Text";
import { useCapabilities, useCurrentUser } from "@/features/account/useCurrentUser";

/**
 * Background-access authorization for flows that run with nobody watching (scheduled and webhook triggers; ported
 * from the old UI's ScheduledFlowAuthControl). Such a run can call delegated tools (email, SharePoint, Google Drive)
 * on the owner's behalf; that needs an explicit consent.
 *
 * Backend-gated: shown only when delegated-token caching is on org-wide (the `automatedSyncEnabled` capability)
 * and the flow has a scheduled trigger. The status is user-scoped (one authorization covers all of the user's
 * scheduled flows). The backend also says how to consent: from the sign-in ("session", Microsoft) or through a
 * Google consent window ("google_code", MHS-1024).
 */
export function FlowAuthorizationCard({
  flow,
  askToAuthorize = 0,
}: Readonly<{
  flow: FlowResponse;
  /** Changes when a schedule was just saved: open the consent dialog if background access isn't authorized. */
  askToAuthorize?: number;
}>) {
  const { t } = useTranslation("workflows");
  const toast = useToast();
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const email = useCurrentUser().data?.user.email;
  const [consentOpen, setConsentOpen] = useState(false);

  // Scheduled and webhook runs both act as the owner (a webhook run's write actions go through approval cards).
  const hasScheduledTrigger = (flow.triggers ?? []).some(
    (tr) => tr.triggerType === "scheduled" || tr.triggerType === "webhook",
  );
  const eligible = capabilities.automatedSyncEnabled && hasScheduledTrigger;

  const auth = useQuery({
    ...getFlowAuthorizationOptions({ path: { flow_uuid: flow.uuid } }),
    enabled: eligible,
  });

  const authorize = useMutation({
    ...authorizeFlowMutation(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getFlowAuthorizationQueryKey({ path: { flow_uuid: flow.uuid } }),
      });
      setConsentOpen(false);
      toast.success(t("auth.authorized"));
    },
    onError: (e) => toast.error(errorMessage(e, t("auth.authorizeFailed"))),
  });

  const status = auth.data?.data.status;
  // A schedule was just saved (Microsoft captures access silently then; Google can't, it needs the consent window).
  // Asked once per save, and only once the status for the saved schedule has loaded.
  const asked = useRef(0);
  useEffect(() => {
    if (askToAuthorize === 0 || askToAuthorize === asked.current || !eligible || auth.isFetching) return;
    asked.current = askToAuthorize;
    if (status === "not_authorized" || status === "needs_reauth") setConsentOpen(true);
  }, [askToAuthorize, eligible, auth.isFetching, status]);

  // Hide entirely when not eligible, while loading, or when the feature is off.
  if (!eligible || auth.isPending) return null;
  if (!status || status === "disabled") return null;

  const authorized = status === "authorized";
  const needsReauth = status === "needs_reauth";
  const google = auth.data?.data.consent === "google_code";
  const authorizeWith = (googleCode?: string) =>
    authorize.mutate({ path: { flow_uuid: flow.uuid }, body: googleCode ? { googleCode } : undefined });

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2">
          <Shield size={16} aria-hidden className="mt-0.5 shrink-0 text-content-secondary" />
          <div className="flex min-w-0 flex-col gap-1">
            <Text variant="body">{t("auth.title")}</Text>
            <Text variant="body-sm" tone="tertiary">
              {authorized ? t("auth.authorizedBody") : t("auth.body")}
            </Text>
            {needsReauth && (
              <Text variant="caption" tone="warning">
                {t("auth.reauthNote")}
              </Text>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {authorized ? (
            <Badge variant="success">{t("auth.authorized")}</Badge>
          ) : (
            <Button variant="primary" size="sm" onClick={() => setConsentOpen(true)}>
              {needsReauth ? t("auth.reauthorize") : t("auth.authorize")}
            </Button>
          )}
        </div>
      </div>

      <Modal
        open={consentOpen}
        onClose={() => setConsentOpen(false)}
        title={t("auth.consentTitle")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConsentOpen(false)}>
              {t("triggers.cancel")}
            </Button>
            {google ? (
              <GoogleConsentButton
                scopes={auth.data?.data.googleScopes ?? []}
                hint={email}
                loading={authorize.isPending}
                onCode={authorizeWith}
                onError={(reason) =>
                  toast.error(t(reason === "unavailable" ? "auth.googleUnavailable" : "auth.googleFailed"))
                }
              >
                {t("auth.consentGoogleAction")}
              </GoogleConsentButton>
            ) : (
              <Button variant="primary" loading={authorize.isPending} onClick={() => authorizeWith()}>
                {t("auth.consentAction")}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <Text variant="body-sm" tone="secondary">
            {t("auth.consentBody")}
          </Text>
          {google && (
            <Text variant="body-sm" tone="secondary">
              {t("auth.consentGoogle")}
            </Text>
          )}
          <Text variant="caption" tone="tertiary">
            {t("auth.consentNote")}
          </Text>
        </div>
      </Modal>
    </Card>
  );
}
